import { BarChart3, Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useInViewRef } from "./shared";

const LOOP_MS = 7000;

function CountUp({
  target,
  start,
  durationMs = 1600,
  prefix = "",
  suffix = "",
}: {
  target: number;
  start: boolean;
  durationMs?: number;
  prefix?: string;
  suffix?: string;
}) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!start) {
      setV(0);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - t0) / durationMs, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, durationMs]);
  return (
    <>
      {prefix}
      {v}
      {suffix}
    </>
  );
}

const KPIS = [
  { label: "Leads", value: 400 },
  { label: "Quotes", value: 312 },
  { label: "Phone calls", value: 178 },
  { label: "Conversions", value: 165 },
];

const MONTHS = [
  { label: "Jan", before: 20, after: 100 },
  { label: "Feb", before: 22, after: 110 },
  { label: "Mar", before: 24, after: 120 },
];

const SOURCES = [
  { label: "Organic Search", pct: 43 },
  { label: "Google Business Profile", pct: 28 },
  { label: "Direct", pct: 17 },
  { label: "Referral", pct: 12 },
];

// donut segments (high 72, medium 18, low 10) — total 100
const DONUT = [
  { pct: 72, color: "#a855f7" },
  { pct: 18, color: "#3b82f6" },
  { pct: 10, color: "#ede9fe" },
];

function Donut({ start }: { start: boolean }) {
  const r = 28;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="relative h-[88px] w-[88px]">
      <svg viewBox="0 0 80 80" className="h-full w-full -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#f1f5f9" strokeWidth="10" />
        {DONUT.map((seg, i) => {
          const len = (seg.pct / 100) * c;
          const el = (
            <circle
              key={i}
              cx="40"
              cy="40"
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth="10"
              strokeDasharray={`${start ? len : 0} ${c}`}
              strokeDashoffset={-offset}
              style={{ transition: `stroke-dasharray 1.2s ease-out ${i * 0.2}s` }}
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[11px] sm:text-sm font-bold text-slate-900 leading-none">
          <CountUp target={72} start={start} suffix="%" />
        </span>
      </div>
    </div>
  );
}

export function PerformanceVisual() {
  const { ref, inView } = useInViewRef<HTMLDivElement>();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setTick((t) => t + 1), LOOP_MS);
    return () => clearInterval(id);
  }, [inView]);

  const loopKey = `${inView}-${tick}`;
  const maxBar = 150;
  // chart geometry
  const chartH = 110;

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden rounded-xl bg-white p-4 text-[11px] text-slate-900 shadow-sm sm:p-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-md">
            <BarChart3 className="h-4 w-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold leading-tight text-slate-900">Performance Overview</div>
            <div className="truncate text-[9px] text-slate-500">Lead performance after website redesign</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[9px] font-medium text-slate-700 whitespace-nowrap">
            <CalendarIcon className="h-3 w-3" />
            <span className="sm:hidden">1/1 – 3/31/26</span>
            <span className="hidden sm:inline">Jan 1 – Mar 31, 2026</span>
            <ChevronDown className="h-3 w-3" />
          </div>
          <span className="text-[8px] text-slate-400 whitespace-nowrap">
            <span className="sm:hidden">Compare: 10/1 – 12/31/25</span>
            <span className="hidden sm:inline">Compare: Oct 1 – Dec 31, 2025</span>
          </span>
        </div>
      </div>

      {/* KPI cards */}
      <div className="mt-3 grid grid-cols-4 gap-1.5">
        {KPIS.map((k, i) => (
          <div
            key={k.label}
            className={`rounded-lg border border-slate-200 p-2 transition-all duration-500 ${
              inView ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
            }`}
            style={{ transitionDelay: `${i * 100}ms` }}
          >
            <div className="text-[9px] font-semibold text-slate-700">{k.label}</div>
            <div className="mt-0.5 text-base font-bold text-emerald-500">
              <CountUp key={`kpi-${i}-${loopKey}`} target={k.value} start={inView} prefix="+" suffix="%" />
            </div>
            <div className="mt-0.5 text-[8px] text-slate-400">vs. Oct 1 – Dec 31, 2025</div>
          </div>
        ))}
      </div>

      {/* Bar chart */}
      <div className="mt-3 rounded-lg border border-slate-200 p-2">
        <div className="text-[10px] font-semibold text-slate-800">Lead volume by month</div>
        <div className="mt-1 flex items-end gap-2" style={{ height: chartH }}>
          {/* Y axis */}
          <div className="flex h-full flex-col justify-between pr-1 text-[7px] text-slate-400">
            {[150, 120, 90, 60, 30, 0].map((v) => (
              <span key={v}>{v}</span>
            ))}
          </div>
          {/* Bars */}
          <div className="flex flex-1 items-end justify-around border-b border-l border-slate-200 pl-1">
            {MONTHS.map((m, i) => {
              const bH = (m.before / maxBar) * (chartH - 14);
              const aH = (m.after / maxBar) * (chartH - 14);
              return (
                <div key={m.label} className="flex flex-col items-center gap-0.5">
                  <div className="flex items-end gap-1">
                    {/* before */}
                    <div className="flex flex-col items-center">
                      <span className="text-[8px] font-bold text-slate-700">{m.before}</span>
                      <div
                        className="w-8 sm:w-10 rounded-t bg-slate-300 transition-all duration-700"
                        style={{
                          height: inView ? bH : 0,
                          transitionDelay: `${i * 150}ms`,
                        }}
                      />
                    </div>
                    {/* after */}
                    <div className="flex flex-col items-center">
                      <span className="text-[8px] font-bold text-slate-700">{m.after}</span>
                      <div
                        className="w-8 sm:w-10 rounded-t bg-gradient-to-t from-blue-500 to-violet-500 transition-all duration-700"
                        style={{
                          height: inView ? aH : 0,
                          transitionDelay: `${i * 150 + 200}ms`,
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-[8px] font-medium text-slate-600">{m.label}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-1 flex items-center justify-center gap-3 text-[8px] text-slate-500">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-slate-300" /> Before redesign
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-violet-500" /> After redesign
          </span>
        </div>
      </div>

      {/* Bottom row: donut + sources */}
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <div className="rounded-lg border border-slate-200 p-2">
          <div className="text-[10px] font-semibold text-slate-800">Lead quality</div>
          <div className="mt-1 flex items-center gap-2">
            <Donut key={`donut-${loopKey}`} start={inView} />
            <div className="flex flex-1 flex-col gap-1 text-[8px] sm:text-[11px] sm:gap-1.5">
              <div className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                <span className="text-slate-700">High-quality</span>
                <span className="ml-auto font-semibold text-slate-900">72%</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                <span className="text-slate-700">Medium-quality</span>
                <span className="ml-auto font-semibold text-slate-900">18%</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-200" />
                <span className="text-slate-700">Low-quality</span>
                <span className="ml-auto font-semibold text-slate-900">10%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 p-2">
          <div className="text-[10px] font-semibold text-slate-800">Top lead sources</div>
          <div className="mt-1.5 flex flex-col gap-1">
            {SOURCES.map((s, i) => (
              <div key={s.label} className="grid grid-cols-[1fr_auto] items-center gap-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-20 truncate text-[8px] text-slate-700">{s.label}</span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-1000"
                      style={{
                        width: inView ? `${s.pct}%` : "0%",
                        transitionDelay: `${i * 150 + 600}ms`,
                      }}
                    />
                  </div>
                </div>
                <span className="text-[8px] font-semibold text-slate-900">{s.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
